import {
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  Notice,
  TextLink,
} from '@upside/flux-ui';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { z } from 'zod';

export type ResetPasswordFormSubmitHandler = (data: {
  password: string;
  passwordConfirm: string;
  showPassword: boolean;
}) => Promise<void>;

export class FormError extends Error {
  errorType: 'password' | 'passwordConfirm' | 'showPassword' | 'root';

  constructor(
    errorType: 'password' | 'passwordConfirm' | 'showPassword' | 'root',
    message: string,
  ) {
    super(message);
    this.name = 'FormError';
    this.errorType = errorType;
  }
}

type ResetPasswordFormProps = {
  onSubmit: ResetPasswordFormSubmitHandler;
  submitText: string;
  isNewUser?: boolean;
};

export function ResetPasswordForm({
  onSubmit,
  submitText,
  isNewUser = false,
}: ResetPasswordFormProps) {
  const { t: translate } = useTranslation('resetPassword');

  const formSchema = z
    .object({
      password: z
        .string({
          error: translate('passwordValidation'),
        })
        .min(16, translate('passwordValidation')),
      passwordConfirm: z.string(),
      showPassword: z.boolean(),
    })
    .superRefine(({ passwordConfirm, password }, formContext) => {
      if (password !== passwordConfirm) {
        formContext.addIssue({
          path: ['passwordConfirm'],
          code: 'custom',
          message: translate('passwordMismatch'),
        });
      }
    });

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      password: '',
      passwordConfirm: '',
      showPassword: false,
    },
    resolver: zodResolver(formSchema),
  });

  const { formState, setError } = form;

  const { isSubmitting, errors } = formState;

  const handleSubmit: ResetPasswordFormSubmitHandler = async (data) => {
    try {
      await onSubmit(data);
    } catch (error) {
      if (error instanceof FormError) {
        setError(error.errorType, {
          message: error.message,
        });
      } else {
        setError('root', { message: translate('error') });
      }
    }
  };

  return (
    <div className="flex flex-col items-center gap-8">
      <Card className="relative flex w-full max-w-md flex-col gap-6 rounded-xl p-8 shadow-md">
        <h1 className="text-center text-2xl font-medium text-foreground">
          {isNewUser
            ? translate('createPassword')
            : translate('createNewPassword')}
        </h1>

        <Notice intent="info">
          <Notice.Content>
            <Notice.Icon />
            <Notice.Caption>{translate('description')}</Notice.Caption>
          </Notice.Content>
        </Notice>

        {errors.root && (
          <Notice intent="error">
            <Notice.Content>
              <Notice.Icon />
              <Notice.Caption>{errors.root.message}</Notice.Caption>
            </Notice.Content>
          </Notice>
        )}

        <Form {...form}>
          <form
            className="flex flex-col gap-6"
            onSubmit={form.handleSubmit((data) => handleSubmit(data))}
          >
            <Form.Field
              control={form.control}
              name="password"
              render={({ field }) => (
                <Form.Item>
                  <Form.Label>
                    {isNewUser
                      ? translate('password')
                      : translate('newPassword')}
                  </Form.Label>
                  <Form.Control>
                    <Input
                      size="xl"
                      {...field}
                      type={form.watch('showPassword') ? 'text' : 'password'}
                      autoComplete="off"
                    />
                  </Form.Control>
                  <Form.Message>{translate('passwordValidation')}</Form.Message>
                </Form.Item>
              )}
            />
            <div className="flex flex-col gap-2">
              <Form.Field
                control={form.control}
                name="passwordConfirm"
                render={({ field }) => (
                  <Form.Item>
                    <Form.Label>{translate('confirmPassword')}</Form.Label>
                    <Form.Control>
                      <Input
                        size="xl"
                        {...field}
                        type={form.watch('showPassword') ? 'text' : 'password'}
                        autoComplete="off"
                      />
                    </Form.Control>
                    <Form.Message />
                  </Form.Item>
                )}
              />
              <Form.Field
                control={form.control}
                name="showPassword"
                render={({ field }) => (
                  <Form.Item>
                    <Form.Control>
                      <Checkbox id="show-password">
                        <Checkbox.Indicator
                          checked={field.value}
                          disabled={isSubmitting}
                          onCheckedChange={field.onChange}
                        />
                        <Checkbox.Label>
                          {translate('showPassword')}
                        </Checkbox.Label>
                      </Checkbox>
                    </Form.Control>
                  </Form.Item>
                )}
              />
            </div>
            <Button
              className="w-full items-center justify-center"
              loading={isSubmitting}
              size="lg"
              intent="primary"
              type="submit"
            >
              <Button.Label>{submitText}</Button.Label>
            </Button>
          </form>
        </Form>
      </Card>

      <TextLink asChild>
        <Link to="/login" role="link">
          {translate('cancel')}
        </Link>
      </TextLink>
    </div>
  );
}
